import { updateOrder, placeLocalOrderOnOfflineMap, clearLocalOrderOfflineMapPlacement,
  getOrder, getLocalOrder } from '@/api/orders';

// Renderer-lifetime custody survives screen replacement. Only target/scope and
// outcome metadata are retained, never credentials, notes or complete orders.
const claims = new Set();
const observers = new Set();
const emit = () => observers.forEach(fn => fn());
const validOrder = item => typeof item?.id === 'string' && typeof item.delivery_date === 'string'
  && ['new', 'accepted', 'designed', 'on_route', 'delivered', 'failed'].includes(item.status);
const coordinate = value => Number.isFinite(value?.latitude) && Math.abs(value.latitude) <= 90
  && Number.isFinite(value?.longitude) && Math.abs(value.longitude) <= 180;
const local = kind => kind !== 'note';
const matches = (item, id, kind, value) => validOrder(item) && item.id === id && (kind === 'note'
  ? (item.dispatch_note ?? '') === value
  : item.desktop_sync?.origin === 'local' && (kind === 'place'
    ? coordinate(item.desktop_sync.offline_map) && item.desktop_sync.offline_map.latitude === value.latitude
      && item.desktop_sync.offline_map.longitude === value.longitude
    : !item.desktop_sync.offline_map && typeof item.desktop_sync.offline_map_cleared_at === 'string'));
function refusal(error) {
  if ([401, 403].includes(error?.response?.status)) return 'Permission unavailable. Review current access.';
  const detail = error?.response?.data?.detail;
  if ([400, 404, 409, 422].includes(error?.response?.status) && [
    'Order not found', 'Unsynchronized local order not found', 'Local order changed during placement',
    'This record changed before your update completed. Refresh and try again.',
    'This order is assigned to a run. Remove it from the run before removing its map placement.',
  ].includes(detail)) return detail;
  return null;
}

export function createDispatchDecisions(capture, read) {
  let generation = null;
  let snapshot = [];
  const listeners = new Set();
  const reviews = new Map();
  const ownerFor = claim => capture(local(claim.kind));
  const related = claim => {
    const owner = capture();
    return owner?.admin && owner.endpoint === claim.endpoint && owner.tenant === claim.tenant;
  };
  const find = id => [...claims].find(claim => claim.id === id && related(claim));
  const notify = () => {
    snapshot = [...claims].filter(related).map(claim => ({ id: claim.id, date: claim.date,
      state: claim.state, stalled: claim.stalled, message: claim.message,
      review: reviews.get(claim)?.message, canAcknowledge: reviews.get(claim)?.ready === true }));
    listeners.forEach(fn => fn());
  };
  return {
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    getSnapshot: () => snapshot,
    activate() { generation = {}; observers.add(notify); notify(); },
    retire() { generation = null; reviews.clear(); observers.delete(notify); },
    blocked: id => !!find(id),
    async run(order, kind, value, { current = () => true, onAccepted = () => {} } = {}) {
      const owner = capture(local(kind)); const epoch = generation;
      if (!epoch || !owner?.admin || !owner.current() || !current() || find(order?.id)
        || !validOrder(order) || !['note', 'place', 'clear'].includes(kind)
        || (kind === 'note' ? owner.offline || typeof value !== 'string'
          : !owner.offline || !owner.canPlace || order.desktop_sync?.origin !== 'local'
            || !['accepted', 'designed'].includes(order.status)
            || (kind === 'place' && !coordinate(value)))) return { state: 'blocked' };
      const claim = { endpoint: owner.endpoint, tenant: owner.tenant, id: order.id,
        date: order.delivery_date, kind, state: 'pending' };
      claims.add(claim); emit();
      const owns = () => generation === epoch && owner.current() && current();
      if (!owns()) { claims.delete(claim); emit(); return { state: 'retired' }; }
      const timer = setTimeout(() => { claim.stalled = true; emit(); }, 30000);
      let item; let accepted = false; let refused;
      try {
        item = kind === 'note' ? await updateOrder(order.id, { dispatch_note: value }, owner.config)
          : kind === 'place' ? await placeLocalOrderOnOfflineMap(order.id, value, owner.config)
            : await clearLocalOrderOfflineMapPlacement(order.id, owner.config);
        accepted = matches(item, order.id, kind, value);
      } catch (error) { refused = refusal(error); }
      finally {
        clearTimeout(timer);
        if (refused) claims.delete(claim);
        else {
          claim.state = accepted ? 'accepted' : 'unknown';
          claim.message = accepted ? kind === 'note' ? 'Flag saved.' : 'Placement saved locally; awaiting synchronization.'
            : 'Outcome unknown. Check current order before another attempt.';
        }
        emit();
      }
      if (!owns()) return { state: 'retired' };
      // Publication is a separate boundary from acknowledgement, never rollback.
      if (accepted) onAccepted(item);
      return { state: accepted ? 'accepted' : refused ? 'refused' : 'unknown', message: refused || claim.message };
    },
    refreshed(ids) {
      if (!generation) return;
      ids.forEach(id => {
        const claim = find(id);
        if (claim?.state === 'accepted' && ownerFor(claim)?.current()) claims.delete(claim);
      });
      emit();
    },
    async review(id) {
      const claim = find(id); const epoch = generation; const owner = claim && ownerFor(claim);
      if (!epoch || claim?.state !== 'unknown' || !owner?.current()) return;
      const attempt = {}; reviews.set(claim, attempt); notify();
      const owns = () => generation === epoch && owner.current() && claims.has(claim) && reviews.get(claim) === attempt;
      try {
        const item = await read(config => {
          if (!owns()) throw new Error('Retired');
          return (local(claim.kind) ? getLocalOrder : getOrder)(id, config);
        }, owner.config);
        if (!owns()) return;
        if (!validOrder(item) || item.id !== id) throw new Error('Invalid observation');
        reviews.set(claim, { current: owner.current, ready: true,
          message: 'Current order checked. This does not prove which attempt saved it. Acknowledge and refresh before a deliberate new action.' });
      } catch (error) {
        if (!owns()) return;
        const absent = error?.response?.status === 404 && error.response.data?.detail === 'Order not found';
        reviews.set(claim, { current: owner.current, ready: absent, message: absent
          ? 'Order is currently absent. This does not establish the previous save outcome.'
          : 'Current order could not be checked. Retry remains blocked.' });
      }
      notify();
    },
    acknowledge(id) {
      const claim = find(id); const review = reviews.get(claim);
      if (!generation || claim?.state !== 'unknown' || !review?.ready || !review.current() || !ownerFor(claim)?.current()) return false;
      claims.delete(claim); reviews.delete(claim); emit(); return true;
    },
  };
}
